import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const AddUserLayer = () => {
    const navigate = useNavigate();

    const [imagePreviewUrl, setImagePreviewUrl] = useState('');
    const [formData, setFormData] = useState({
        username: '',
        fullname: '',
        password: '',
        role: 'user',
        status: 'active',
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [validationErrors, setValidationErrors] = useState({});

    // Check if user is admin
    useEffect(() => {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
            try {
                const userData = JSON.parse(storedUser);
                if (userData.role !== 'admin') {
                    // Redirect non-admin users to dashboard
                    navigate('/', { replace: true });
                }
            } catch (error) {
                console.error('Error parsing user data:', error);
                navigate('/', { replace: true });
            }
        } else {
            navigate('/sign-in', { replace: true });
        }
    }, [navigate]);

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreviewUrl(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
        // Clear validation error for this field
        if (validationErrors[name]) {
            setValidationErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setValidationErrors({});

        const apiUrl = process.env.REACT_APP_API_URL;
        const token = localStorage.getItem('authToken');

        if (!apiUrl || !token) {
            setError('Missing API URL or authentication token');
            return;
        }

        try {
            setIsSubmitting(true);

            const response = await fetch(`${apiUrl}/users`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(formData),
            });

            const data = await response.json();

            if (!response.ok) {
                if (data.errors) {
                    setValidationErrors(data.errors);
                }
                throw new Error(data.message || 'Failed to create user');
            }

            if (data.success) {
                // Redirect to users list on success
                navigate('/users-list');
            } else {
                throw new Error(data.message || 'Failed to create user');
            }
        } catch (err) {
            console.error(err);
            setError(err instanceof Error ? err.message : 'Unexpected error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancel = () => {
        navigate('/users-list');
    };

    return (
        <div className="card h-100 p-0 radius-12">
            <div className="card-body p-24">
                <div className="row justify-content-center">
                    <div className="col-xxl-6 col-xl-8 col-lg-10">
                        <div className="card border">
                            <div className="card-body">
                              
                                {/* Upload Image End */}
                                {error && (
                                    <div className="alert alert-danger mb-20" role="alert">
                                        {error}
                                    </div>
                                )}
                                <form onSubmit={handleSubmit} noValidate>
                                    <div className="mb-20">
                                        <label
                                            htmlFor="username"
                                            className="form-label fw-semibold text-primary-light text-sm mb-8"
                                        >
                                            Username <span className="text-danger-600">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-control radius-8 ${
                                                validationErrors.username ? 'is-invalid' : ''
                                            }`}
                                            id="username"
                                            name="username"
                                            placeholder="Enter username"
                                            value={formData.username}
                                            onChange={handleInputChange}
                                            required
                                        />
                                        {validationErrors.username && (
                                            <div className="invalid-feedback">
                                                {validationErrors.username[0]}
                                            </div>
                                        )}
                                    </div>
                                    <div className="mb-20">
                                        <label
                                            htmlFor="fullname"
                                            className="form-label fw-semibold text-primary-light text-sm mb-8"
                                        >
                                            Full Name <span className="text-danger-600">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-control radius-8 ${
                                                validationErrors.fullname ? 'is-invalid' : ''
                                            }`}
                                            id="fullname"
                                            name="fullname"
                                            placeholder="Enter Full Name"
                                            value={formData.fullname}
                                            onChange={handleInputChange}
                                            required
                                        />
                                        {validationErrors.fullname && (
                                            <div className="invalid-feedback">
                                                {validationErrors.fullname[0]}
                                            </div>
                                        )}
                                    </div>
                                    <div className="mb-20">
                                        <label
                                            htmlFor="password"
                                            className="form-label fw-semibold text-primary-light text-sm mb-8"
                                        >
                                            Password <span className="text-danger-600">*</span>
                                        </label>
                                        <input
                                            type="password"
                                            className={`form-control radius-8 ${
                                                validationErrors.password ? 'is-invalid' : ''
                                            }`}
                                            id="password"
                                            name="password"
                                            placeholder="Enter password (min 6 characters)"
                                            value={formData.password}
                                            onChange={handleInputChange}
                                            required
                                        />
                                        {validationErrors.password && (
                                            <div className="invalid-feedback">
                                                {validationErrors.password[0]}
                                            </div>
                                        )}
                                    </div>
                                    <div className="mb-20">
                                        <label
                                            htmlFor="role"
                                            className="form-label fw-semibold text-primary-light text-sm mb-8"
                                        >
                                            Role <span className="text-danger-600">*</span>
                                        </label>
                                        <select
                                            className={`form-control radius-8 form-select ${
                                                validationErrors.role ? 'is-invalid' : ''
                                            }`}
                                            id="role"
                                            name="role"
                                            value={formData.role}
                                            onChange={handleInputChange}
                                            required
                                        >
                                            <option value="user">User</option>
                                            <option value="admin">Admin</option>
                                        </select>
                                        {validationErrors.role && (
                                            <div className="invalid-feedback">
                                                {validationErrors.role[0]}
                                            </div>
                                        )}
                                    </div>
                                    <div className="mb-20">
                                        <label
                                            htmlFor="status"
                                            className="form-label fw-semibold text-primary-light text-sm mb-8"
                                        >
                                            Status <span className="text-danger-600">*</span>
                                        </label>
                                        <select
                                            className={`form-control radius-8 form-select ${
                                                validationErrors.status ? 'is-invalid' : ''
                                            }`}
                                            id="status"
                                            name="status"
                                            value={formData.status}
                                            onChange={handleInputChange}
                                            required
                                        >
                                            <option value="active">Active</option>
                                            <option value="inactive">Inactive</option>
                                        </select>
                                        {validationErrors.status && (
                                            <div className="invalid-feedback">
                                                {validationErrors.status[0]}
                                            </div>
                                        )}
                                    </div>
                                    <div className="d-flex align-items-center justify-content-center gap-3">
                                        <button
                                            type="button"
                                            onClick={handleCancel}
                                            className="border border-danger-600 bg-hover-danger-200 text-danger-600 text-md px-56 py-11 radius-8"
                                            disabled={isSubmitting}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="btn btn-primary border border-primary-600 text-md px-56 py-12 radius-8"
                                            disabled={isSubmitting}
                                        >
                                            {isSubmitting ? 'Saving...' : 'Save'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

    );
};

export default AddUserLayer;